import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import ErrorAlert from '../components/common/ErrorAlert';
import SuccessAlert from '../components/common/SuccessAlert';
import { updateProfile } from '../redux/slices/userSlice';

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading, error } = useSelector((state) => state.users);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    if (e.target.name === 'confirmPassword' || e.target.name === 'newPassword') {
      if (e.target.name === 'confirmPassword' && e.target.value !== formData.newPassword) {
        setPasswordError('Passwords do not match');
      } else if (e.target.name === 'newPassword' && e.target.value !== formData.confirmPassword) {
        setPasswordError('Passwords do not match');
      } else {
        setPasswordError('');
      }
    }
  };

  // Clear success message when there's an error
  useEffect(() => {
    if (error) {
      setSuccessMessage('');
    }
  }, [error]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Current password is required for any changes
    if (!formData.currentPassword) {
      setPasswordError('Current password is required for any changes');
      return;
    }

    const updateData = {
      name: formData.name,
      email: formData.email,
      currentPassword: formData.currentPassword,
      ...(formData.newPassword ? { newPassword: formData.newPassword } : {})
    };

    dispatch(updateProfile(updateData))
      .unwrap()
      .then(() => {
        setSuccessMessage('Profile updated successfully');
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      })
      .catch(() => {});  // Error is handled by the ErrorAlert component
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                bgcolor: 'primary.main',
              }}
            >
              <AccountCircle sx={{ fontSize: 80 }} />
            </Avatar>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {user?.name}
            </Typography>
            <Typography color="textSecondary">
              Role: {user?.role === 'companyAdmin' ? 'Company Admin' : user?.role}
            </Typography>
            {user?.company && (
              <Typography color="textSecondary">
                {user.company.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Current Password (Required)
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                label="Current Password"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange}
                error={Boolean(passwordError)}
                helperText={passwordError}
                required
              />

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                New Password (Optional)
              </Typography>

              <TextField
                fullWidth
                margin="normal"
                label="New Password"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                error={Boolean(passwordError)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={Boolean(passwordError)}
                helperText={passwordError}
              />

              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading || Boolean(passwordError)}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ErrorAlert
        error={error}
        onClose={() => dispatch({ type: 'users/clearError' })}
      />
      <SuccessAlert
        message={successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </Box>
  );
};

export default Profile;
